import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../api/axios';
import { MdVisibility, MdVisibilityOff, MdArrowBack } from 'react-icons/md';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [fpStep, setFpStep] = useState('email'); // 'email' | 'otp' | 'done'
  const [fpEmail, setFpEmail] = useState('');
  const [fpOtp, setFpOtp] = useState('');
  const [fpNewPassword, setFpNewPassword] = useState('');
  const [fpError, setFpError] = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [fpSuccess, setFpSuccess] = useState('');

  const { login, user, loading } = useContext(AuthContext);
  const navigate = useNavigate();

  // If user is already logged in, redirect them to their dashboard
  useEffect(() => {
    if (!loading && user) {
      if (user.role === 'superadmin' || user.role === 'cetadmin') {
        navigate('/super-admin');
      } else if (user.role === 'moderator') {
        navigate('/moderator');
      } else if (user.role === 'examiner') {
        navigate('/examiner');
      } else if (user.role === 'subject_matter_expert') {
        navigate('/subject-matter-expert');
      } else if (user.role === 'cetadmin') {
        navigate('/cet-admin');
      } else {
        // default
        navigate('/');
      }
    }
  }, [loading, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const data = await login(email, password, twoFactorCode);

      // Check if 2FA is required
      if (data.requires2FA) {
        setRequires2FA(true);
        setSubmitting(false);
        return;
      }

      // Redirect based on role
      if (data.user.role === 'superadmin' || data.user.role === 'cetadmin') {
        navigate('/super-admin');
      } else if (data.user.role === 'moderator') {
        navigate('/moderator');
      } else if (data.user.role === 'examiner') {
        navigate('/examiner');
      } else if (data.user.role === 'subject_matter_expert') {
        navigate('/subject-matter-expert');
      } else if (data.user.role === 'cetadmin') {
        navigate('/cet-admin');
      }
    } catch (err) {
      if (err.response?.data?.tempPassword) {
        setError('Please check your email for a link to set your password. You have been sent an onboarding email with instructions.');
      } else {
        setError(err.response?.data?.message || 'Login failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotSendOtp = async (e) => {
    e.preventDefault();
    setFpError('');
    setFpLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: fpEmail });
      setFpStep('otp');
    } catch (err) {
      setFpError(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setFpLoading(false);
    }
  };

  const handleForgotResetPassword = async (e) => {
    e.preventDefault();
    setFpError('');
    setFpLoading(true);
    try {
      const res = await api.post('/auth/reset-password', {
        email: fpEmail,
        otp: fpOtp,
        newPassword: fpNewPassword,
      });
      setFpSuccess(res.data.message);
      setFpStep('done');
    } catch (err) {
      setFpError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setFpLoading(false);
    }
  };

  const closeForgotModal = () => {
    setForgotOpen(false);
    setFpStep('email');
    setFpEmail('');
    setFpOtp('');
    setFpNewPassword('');
    setFpError('');
    setFpSuccess('');
  };

  return (
    <div className="login-container">
      {/* Left Side - Branding & Info */}
      <div className="login-left">
        <div className="login-branding">
          <h1 className="login-brand-title">CET Question Generator</h1>
          <p className="login-brand-subtitle">AI-Powered Examination Management System</p>
        </div>

        <div className="login-features">
          <div className="feature-item">
            <span>Secure Login</span>
          </div>
          <div className="feature-item">
            <span>Quick Access</span>
          </div>
          <div className="feature-item">
            <span>24/7 Available</span>
          </div>
        </div>

        <div className="login-welcome">
          <h2>Welcome Back!</h2>
          <p>Sign in to access your dashboard and manage your examination journey</p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="login-right">
        <div className="login-form-container">
          <div className="login-form-header">
            <img src="/favicon.png" alt="CET Logo" className="form-logo" />
            <h2 className="form-title">Sign In</h2>
            <p className="form-subtitle">Enter your credentials to continue</p>
          </div>

          {error && <div className="form-error-modern">{error}</div>}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group-modern">
              <label className="form-label-modern">
                Email Address
              </label>
              <input
                type="email"
                className="form-input-modern"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                disabled={requires2FA}
              />
            </div>

            <div className="form-group-modern">
              <label className="form-label-modern">
                Password
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input-modern"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  disabled={requires2FA}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  {showPassword ? <MdVisibilityOff aria-hidden="true" /> : <MdVisibility aria-hidden="true" />}
                  <span>{showPassword ? 'Hide' : 'Show'}</span>
                </button>
              </div>
              <div style={{ textAlign: 'right', marginTop: '6px' }}>
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 500, padding: 0 }}
                >
                  Forgot password?
                </button>
              </div>
            </div>

            {requires2FA && (
              <div className="form-group-modern">
                <label className="form-label-modern">
                  Two-Factor Authentication Code
                </label>
                <input
                  type="text"
                  className="form-input-modern code-input"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength="6"
                  required
                  autoFocus
                />
                <p className="input-hint">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>
            )}

            {requires2FA && (
              <button
                type="button"
                className="btn-modern btn-secondary-modern"
                onClick={() => {
                  setRequires2FA(false);
                  setTwoFactorCode('');
                }}
              >
                <MdArrowBack className="ui-icon" aria-hidden="true" /> Back
              </button>
            )}

            <button
              type="submit"
              className="btn-modern btn-primary-modern"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-small"></span>
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* <div className="form-footer">
            <p>Don't have an account? <Link to="/register" className="form-link">Register here</Link></p>
          </div> */}
        </div>
      </div>

      {/* Forgot Password Modal */}
      {forgotOpen && (
        <div className="login-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(17, 24, 39, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '1.5rem', width: '100%', maxWidth: '420px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-lg)' }}>
            <h3 style={{ margin: '0 0 0.5rem', color: 'var(--text-primary)', fontSize: '1.125rem', fontWeight: 600 }}>Reset Password</h3>

            {fpStep === 'email' && (
              <>
                <p style={{ margin: '0 0 1.25rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Enter your email and we'll send you a 6-digit OTP.</p>
                {fpError && <div className="form-error-modern">{fpError}</div>}
                <form onSubmit={handleForgotSendOtp}>
                  <div className="form-group-modern">
                    <label className="form-label-modern">Email Address</label>
                    <input type="email" className="form-input-modern" value={fpEmail} onChange={e => setFpEmail(e.target.value)} placeholder="your.email@example.com" required />
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.625rem', marginTop: '1rem' }}>
                    <button type="button" onClick={closeForgotModal} className="btn-modern btn-secondary-modern" style={{ flex: 1 }}>Cancel</button>
                    <button type="submit" className="btn-modern btn-primary-modern" style={{ flex: 1 }} disabled={fpLoading}>
                      {fpLoading ? 'Sending...' : 'Send OTP'}
                    </button>
                  </div>
                </form>
              </>
            )}

            {fpStep === 'otp' && (
              <>
                <p style={{ margin: '0 0 1.25rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Enter the OTP sent to <strong>{fpEmail}</strong> and your new password.</p>
                {fpError && <div className="form-error-modern">{fpError}</div>}
                <form onSubmit={handleForgotResetPassword}>
                  <div className="form-group-modern">
                    <label className="form-label-modern">OTP Code</label>
                    <input type="text" className="form-input-modern code-input" value={fpOtp} onChange={e => setFpOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength="6" required autoFocus />
                  </div>
                  <div className="form-group-modern">
                    <label className="form-label-modern">New Password</label>
                    <input type="password" className="form-input-modern" value={fpNewPassword} onChange={e => setFpNewPassword(e.target.value)} placeholder="Min. 6 characters" required minLength="6" />
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.625rem', marginTop: '1rem' }}>
                    <button type="button" onClick={() => { setFpStep('email'); setFpError(''); }} className="btn-modern btn-secondary-modern" style={{ flex: 1 }}><MdArrowBack className="ui-icon" aria-hidden="true" /> Back</button>
                    <button type="submit" className="btn-modern btn-primary-modern" style={{ flex: 1 }} disabled={fpLoading}>
                      {fpLoading ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </div>
                </form>
              </>
            )}

            {fpStep === 'done' && (
              <>
                <p style={{ margin: '0 0 1.25rem', color: 'var(--success)', fontSize: '0.875rem' }}>{fpSuccess}</p>
                <button onClick={closeForgotModal} className="btn-modern btn-primary-modern" style={{ width: '100%' }}><MdArrowBack className="ui-icon" aria-hidden="true" /> Back to Login</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
